import { EditableText, EditableTitle } from "@/components/EditableText";
import { Scrollable } from "@/components/Scrollable/Scrollable";
import { BasicWriter } from "@/components/Writer/BasicWriter";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { ArtifactMetadata } from "@/components/display/attributes/ArtifactMetadata";
import { Separator } from "@/components/ui/separator";
import { useKitData } from "@/data/use-kits-data";
import {
  useKitTemplateData,
  useKitTemplatesData,
} from "@/data/use-templates-data";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";

export default function TemplatePage() {
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();

  const { updateKit } = useKitData(groupUuid, kitUuid);
  const { templates } = useKitTemplatesData(groupUuid, kitUuid);
  const templateUuid = templates[0]?.uuid || null;
  const { template, updateTemplate } = useKitTemplateData(
    groupUuid,
    kitUuid,
    templateUuid,
  );

  if (!template) {
    return null;
  }

  const handleUpdateName = async (newName: string) => {
    await updateTemplate({ name: newName });
    await updateKit({ name: newName });
  };

  const handleUpdateDescription = async (newDescription: string) => {
    await updateTemplate({ description: newDescription });
    await updateKit({ description: newDescription });
  };

  const handleUpdateScaffolding = async (newScaffolding: string) => {
    await updateTemplate({ scaffolding: newScaffolding });
  };

  const handleUpdateInstructions = async (newInstructions: string) => {
    await updateTemplate({ instructions: newInstructions });
  };

  return (
    <>
      <Page document>
        <PageHeader document>
          <PageHeader.Title>
            <EditableTitle value={template.name} onUpdate={handleUpdateName} />
          </PageHeader.Title>
        </PageHeader>
        <PageBody innerClassName="overflow-hidden" document>
          <PageBody.Metadata className="justify-between">
            <PageBody.MetadataGroup>
              <EditableText
                value={
                  template.description !== ""
                    ? template.description
                    : "Description..."
                }
                onUpdate={handleUpdateDescription}
              />
            </PageBody.MetadataGroup>
            <PageBody.MetadataGroup>
              <ArtifactMetadata artifact={template} />
            </PageBody.MetadataGroup>
          </PageBody.Metadata>
          <div className="flex flex-row gap-8 overflow-hidden">
            <div className="flex flex-1 flex-col gap-4">
              <h2 className="text-foreground-5 text-lg font-bold">
                Starting Skeleton
              </h2>
              <Separator />
              <BasicWriter
                key={`template-scaffolding-${template.uuid}`}
                content={template.scaffolding}
                onUpdate={handleUpdateScaffolding}
                placeholder="Draft the starting structure for your documents..."
              />
            </div>

            <div className="flex flex-1 flex-col gap-4">
              <h2 className="text-foreground-5 text-lg font-bold">
                AI Instructions
              </h2>
              <Separator />
              <Scrollable outerClassName="flex-1 w-full">
                <BasicWriter
                  key={`template-instructions-${template.uuid}`}
                  content={template.instructions}
                  onUpdate={handleUpdateInstructions}
                  placeholder="How the document should be written and analyzed..."
                />
              </Scrollable>
            </div>
          </div>
        </PageBody>
      </Page>
    </>
  );
}
